import axios from "axios";
import {
  getAccessToken,
  getTenantId,
  getUserInfo,
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalUserInfo,
} from "@/lib/storeManager";

const userApiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

userApiClient.interceptors.request.use(config => {
  const token = getAccessToken();
  const tenantId = getTenantId();

  if (
    token &&
    config.headers &&
    !config.url?.includes("/auth/token-refresh/")
  ) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (tenantId && config.headers) {
    config.headers["X-Tenant-Id"] = tenantId;
  }
  return config;
});

export interface InstitutionListItem {
  id?: string | number;
  name?: string;
  institution_name?: string;
  city?: string;
  district?: string;
  state?: string;
  address?: string;
  address_line1?: string;
  pincode?: string;
  type?: string;
  institution_type?: string;
  total_students?: number;
  capacity?: number;
  sic_rating?: number;
  scholarship_available?: boolean;
  scholarship?: boolean;
  [key: string]: unknown;
}

export interface InstitutionDetailItem extends InstitutionListItem {}

export interface FacilityMaster {
  id: string | number;
  name: string;
  order_by?: number | null;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface State {
  id: string | number;
  name: string;
}

export interface District {
  id: string | number;
  name: string;
  state?: string | number;
}

export interface City {
  id: string | number;
  name: string;
  district?: string | number;
}

const toInstitutionList = (payload: unknown): InstitutionListItem[] => {
  if (Array.isArray(payload)) {
    return payload as InstitutionListItem[];
  }
  if (!payload || typeof payload !== "object") {
    return [];
  }

  const data = payload as Record<string, unknown>;
  if (Array.isArray(data.results)) {
    return data.results as InstitutionListItem[];
  }
  if (Array.isArray(data.data)) {
    return data.data as InstitutionListItem[];
  }
  if (Array.isArray(data.items)) {
    return data.items as InstitutionListItem[];
  }

  return [];
};

const toInstitutionDetail = (payload: unknown): InstitutionDetailItem => {
  if (!payload || typeof payload !== "object") {
    return {} as InstitutionDetailItem;
  }

  const data = payload as Record<string, unknown>;

  if (data.data && typeof data.data === "object") {
    return data.data as InstitutionDetailItem;
  }

  if (data.result && typeof data.result === "object") {
    return data.result as InstitutionDetailItem;
  }

  return data as InstitutionDetailItem;
};

export const publicInstitutionsAPI = {
  getInstitutionsList: async (
    params?: Record<string, unknown> | URLSearchParams
  ) => {
    let finalParams: any = params;
    if (!(params instanceof URLSearchParams)) {
      finalParams = { ...params, _: Date.now() };
    }
    const response = await userApiClient.get("/jbf/institutions-list/", {
      params: finalParams,
    });
    return toInstitutionList(response.data);
  },

  getInstitutionsListPaginated: async (
    params?: Record<string, unknown> | URLSearchParams
  ): Promise<{ data: InstitutionListItem[]; count: number }> => {
    let finalParams: any = params;
    if (!(params instanceof URLSearchParams)) {
      finalParams = { ...params, _: Date.now() };
    }
    const response = await userApiClient.get("/jbf/institutions-list/", {
      params: finalParams,
    });
    const body = response.data as any;
    const data = toInstitutionList(body);
    let count = data.length;
    if (body && typeof body === "object" && typeof body.count === "number") {
      count = body.count;
    }
    return { data, count };
  },

  getInstitutionDetail: async (id: string) => {
    const response = await userApiClient.get<InstitutionDetailItem>(
      `/jbf/institutions-list/${id}/`
    );
    return toInstitutionDetail(response.data);
  },

  /** GET /jbf/states/ — list states for filter dropdowns */
  getStates: async (): Promise<string[]> => {
    const response = await userApiClient.get("/jbf/states/?page_size=1000");
    const raw = response.data;
    const data =
      (raw as Record<string, unknown>).data ??
      (raw as Record<string, unknown>).results ??
      raw;
    if (Array.isArray(data)) {
      return data
        .map((item: unknown) =>
          typeof item === "string"
            ? item
            : ((item as { name?: string })?.name ?? "")
        )
        .filter(Boolean)
        .sort();
    }
    return [];
  },

  /** GET /jbf/cities/ — list cities for filter dropdowns */
  getCities: async (state?: string): Promise<string[]> => {
    const response = await userApiClient.get("/jbf/cities/", {
      params: {
        ...(state ? { state } : {}),
        page_size: 1000,
      },
    });
    const raw = response.data;
    const data =
      (raw as Record<string, unknown>).data ??
      (raw as Record<string, unknown>).results ??
      raw;
    if (Array.isArray(data)) {
      return data
        .map((item: unknown) =>
          typeof item === "string"
            ? item
            : ((item as { name?: string })?.name ?? "")
        )
        .filter(Boolean)
        .sort();
    }
    return [];
  },
};

export const statesAPI = {
  list: async (): Promise<State[]> => {
    const response = await userApiClient.get("/jbf/states/?page_size=1000");
    const raw = response.data;
    return (raw.data ?? raw.results ?? raw) as State[];
  },
  getDistrictsByStateId: async (
    stateId: string | number
  ): Promise<District[]> => {
    const response = await userApiClient.get(
      `/jbf/districts/?state_id=${stateId}&page_size=1000`
    );
    const raw = response.data;
    return (raw.data ?? raw.results ?? raw) as District[];
  },
};

export const districtsAPI = {
  getCitiesByDistrictId: async (
    districtId: string | number
  ): Promise<City[]> => {
    const response = await userApiClient.get(
      `/jbf/cities/?district_id=${districtId}&page_size=1000`
    );
    const raw = response.data;
    return (raw.data ?? raw.results ?? raw) as City[];
  },
};

export const facilitiesMasterAPI = {
  list: async (): Promise<FacilityMaster[]> => {
    const response = await userApiClient.get<
      FacilityMaster[] | { data?: FacilityMaster[]; results?: FacilityMaster[] }
    >("/jbf/facilities-master/?page_size=1000");
    const payload = response.data;
    if (Array.isArray(payload)) {
      return payload;
    }
    if (Array.isArray(payload?.data)) {
      return payload.data;
    }
    if (Array.isArray(payload?.results)) {
      return payload.results;
    }
    return [];
  },
};

// ============================================================================
// Donations API (Razorpay flow: create-order -> checkout -> verify-payment)
// ============================================================================

export interface DonationCreateOrderPayload {
  /** Donation scheme selected by the user (from /jbf/donation-schemes/) */
  scheme_id?: string | number;
  name: string;
  email: string;
  phone: string;
  donation_type: string;
  amount: string;
  organization?: string;
  pan_number?: string;
  aadhaar_number?: string;
  message?: string;
  address_line1: string;
  address_line2?: string;
  state: string | number;
  district: string | number;
  city: string | number;
  pincode: string;
  mode: string;
}

export interface DonationCreateOrderResponse {
  order_id: string;
  amount: number;
  currency?: string;
  key?: string;
  key_id?: string;
}

/** Backend create-order raw response: { message, status_code, data: { order_id, amount, ... } } */
interface DonationCreateOrderApiResponse {
  message?: string;
  status_code?: number;
  data?: {
    donation_id?: number;
    order_id?: string;
    amount?: number;
    currency?: string;
    key_id?: string;
  };
}

/** Payload for verify-payment: exactly these three values from Razorpay checkout success response */
export interface DonationVerifyPaymentPayload {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface DonationAddPayload {
  name: string;
  email: string;
  phone: string;
  organization: string;
  donation_type: string;
  amount: string | number;
  mode: string;
  payment_mode: string;
  pan_number: string;
  aadhaar_number?: string | null;
  message: string;
  address_line1: string;
  address_line2?: string;
  state?: string | number;
  district?: string | number;
  city?: string | number;
  pincode?: string;
}

export const donationsAPI = {
  /** POST /jbf/donations/create-order/ — create Razorpay order; returns order_id and amount (paise) */
  createOrder: async (
    payload: DonationCreateOrderPayload
  ): Promise<DonationCreateOrderResponse> => {
    const { data } = await userApiClient.post<DonationCreateOrderApiResponse>(
      "/jbf/donations/create-order/",
      payload
    );
    const inner = data?.data;
    return {
      order_id: inner?.order_id ?? "",
      amount: inner?.amount ?? 0,
      currency: inner?.currency,
      key_id: inner?.key_id,
    };
  },

  /** POST /jbf/donations/verify-payment/ — verify after Razorpay success; payload: razorpay_order_id, razorpay_payment_id, razorpay_signature */
  verifyPayment: async (
    payload: DonationVerifyPaymentPayload
  ): Promise<unknown> => {
    const body: DonationVerifyPaymentPayload = {
      razorpay_order_id: payload.razorpay_order_id,
      razorpay_payment_id: payload.razorpay_payment_id,
      razorpay_signature: payload.razorpay_signature,
    };
    const { data } = await userApiClient.post<unknown>(
      "/jbf/donations/verify-payment/",
      body
    );
    return data;
  },

  /** POST /jbf/donations/add/ — record donation after successful online payment */
  addDonation: async (payload: DonationAddPayload): Promise<unknown> => {
    const { data } = await userApiClient.post<unknown>(
      "/jbf/donations/add/",
      payload
    );
    return data;
  },
};

// ============================================================================
// Blogs API (public list for user portal)
// ============================================================================

export interface BlogListItem {
  id: number;
  title: string;
  sub_title: string | null;
  description: string | null;
  category: number;
  category_name: string | null;
  date: string | null;
  is_enabled: boolean;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  banner: string | null;
  video: string | null;
}

export interface BlogListResponse {
  message: string;
  status_code: number;
  data: BlogListItem[];
}

export interface BlogDetailItem {
  id: number;
  title: string;
  sub_title: string | null;
  description: string | null;
  category: number;
  category_name: string | null;
  date: string | null;
  is_enabled: boolean;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  banner: string | null;
  video: string | null;
}

export const blogsAPI = {
  /** GET /jbf/blogs/ — list blogs. Cache-busted via query param only (no custom headers) to avoid CORS preflight. */
  getBlogList: async (
    params?: Record<string, unknown>
  ): Promise<BlogListItem[]> => {
    const response = await userApiClient.get<BlogListResponse>("/jbf/blogs/", {
      params: { ...params, _: Date.now() },
    });
    const data = response.data?.data;
    return Array.isArray(data) ? data : [];
  },

  getBlogDetail: async (id: string): Promise<BlogDetailItem> => {
    const response = await userApiClient.get<{
      message?: string;
      status_code?: number;
      data?: BlogDetailItem;
    }>(`/jbf/blogs/${id}/`);
    const body = response.data as
      | { data?: BlogDetailItem }
      | BlogDetailItem
      | null
      | undefined;
    if (body && typeof body === "object" && "data" in body && body.data) {
      return body.data;
    }
    return (body ?? {}) as BlogDetailItem;
  },
};

// ============================================================================
// Vendors API (public list & detail for user portal)
// ============================================================================

export interface VendorListItem {
  id: number;
  logo: string | null;
  vendor_name: string;
  category: string | string[]; // API may send JSON string or array
  company_name: string | null;
  vendor_type: string | null;
  short_description: string | null;
  city: string | null;
  state: string | null;
  pin_code: string | null;
  verification_status: string | null;
  available_for_work?: boolean | null;
  [key: string]: unknown;
}

export interface VendorDetailItem extends VendorListItem {
  contact_person_name?: string | null;
  mobile_number?: string | null;
  email?: string | null;
  office_address?: string | null;
  service_areas?: string | string[] | null;
  pan_india_service?: boolean | null;
  services_offered?: string | string[] | null;
  specialization?: string | null;
  years_of_experience?: number | null;
  hostel_projects_experience?: boolean | null;
  past_projects?: string | string[] | null;
  empanelment_status?: string | null;
  compliance_confirmation?: boolean | null;
  admin_notes?: string | null;
  working_days?: string | string[] | null;
  service_timings?: string | null;
  documents_uploaded?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

function parseVendorCategory(
  value: string | string[] | null | undefined
): string {
  if (value === null) {
    return "—";
  }
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "—";
  }
  if (typeof value !== "string") {
    return "—";
  }
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? parsed.join(", ") : value;
  } catch {
    return value || "—";
  }
}

export interface VendorListResponse {
  data: VendorListItem[];
  count: number;
}

export const vendorsAPI = {
  getVendorList: async (
    params?: Record<string, unknown>
  ): Promise<VendorListResponse> => {
    const search = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          if (Array.isArray(value)) {
            value.forEach(v => search.append(key, String(v)));
          } else {
            search.append(key, String(value));
          }
        }
      });
    }
    search.set("_", String(Date.now())); // disk cache bust
    const query = search.toString();
    const url = `/jbf/vendors/?${query}`;
    const response = await userApiClient.get<{
      message?: string;
      status_code?: number;
      count?: number;
      data?: VendorListItem[];
    }>(url);
    const body = response.data;
    if (!body || typeof body !== "object") {
      return { data: [], count: 0 };
    }
    const data = Array.isArray((body as { data?: VendorListItem[] }).data)
      ? (body as { data: VendorListItem[] }).data
      : [];
    const count =
      typeof (body as { count?: number }).count === "number"
        ? (body as { count: number }).count
        : data.length;
    return { data, count };
  },

  getVendorDetail: async (id: string): Promise<VendorDetailItem> => {
    const response = await userApiClient.get<{
      message?: string;
      status_code?: number;
      data?: VendorDetailItem;
    }>(`/jbf/vendors/${id}/`);
    const body = response.data as
      | { data?: VendorDetailItem }
      | VendorDetailItem
      | null
      | undefined;
    if (body && typeof body === "object" && "data" in body && body.data) {
      return body.data as VendorDetailItem;
    }
    if (body && typeof body === "object" && "id" in body) {
      return body as VendorDetailItem;
    }
    throw new Error("Vendor detail not found");
  },
};

// ============================================================================
// FAQs & Testimonials API (public)
// ============================================================================

export interface FAQQuestion {
  id: number;
  category: number;
  category_title: string;
  question: string;
  answer: string;
  is_enabled: boolean;
  order_by: number;
  created_at?: string;
  updated_at?: string;
}

export interface FAQCategory {
  id: number | string;
  title: string;
  questions: FAQQuestion[];
}

export interface Testimonial {
  id: number;
  category: string;
  name: string;
  designation: string;
  description: string;
  is_enabled: boolean;
  order_by: number;
  created_at?: string;
  updated_at?: string;
}

export const faqAPI = {
  getFAQList: async (): Promise<FAQQuestion[]> => {
    const response = await userApiClient.get<{ data: FAQQuestion[] }>(
      "/jbf/faq-questions/",
      {
        params: { _: Date.now() },
      }
    );
    const data = response.data?.data;
    return Array.isArray(data) ? data.filter(q => q.is_enabled) : [];
  },
};

export const testimonialsAPI = {
  getTestimonialList: async (): Promise<Testimonial[]> => {
    const response = await userApiClient.get<{ data: Testimonial[] }>(
      "/jbf/testimonials/",
      {
        params: { _: Date.now() },
      }
    );
    const data = response.data?.data;
    return Array.isArray(data) ? data.filter(t => t.is_enabled) : [];
  },
};

export interface ContactFormPayload {
  name: string;
  email: string;
  phone_number: string;
  subject: string;
  message: string;
}

export const contactFormAPI = {
  submit: async (payload: ContactFormPayload): Promise<void> => {
    await userApiClient.post("/jbf/contact-forms/", payload);
  },
};

// ============================================================================
// Events API (Public List)
// ============================================================================

export interface JBFEvent {
  id: number | string;
  event_for: string;
  address: string;
  event_title: string;
  start_date: string;
  end_date: string;
  image?: string;
  date?: string;
  number_of_days: number | string;
  description: string;
  created_at?: string;
  updated_at?: string;
}

export const eventsAPI = {
  /** GET /jbf/events/ — list all events. */
  getEventList: async (
    params?: Record<string, unknown>
  ): Promise<JBFEvent[]> => {
    const response = await userApiClient.get<{ data: JBFEvent[] }>(
      "/jbf/events/",
      {
        params: { ...params, _: Date.now() },
      }
    );
    const data = response.data?.data;
    return Array.isArray(data) ? data : [];
  },
};

// ============================================================================
// Media Gallery API (Public List)
// ============================================================================

export interface GalleryCategory {
  id: number | string;
  name: string;
  display_order: number;
  is_enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface GalleryItem {
  id: number | string;
  category: number | string;
  category_name?: string;
  title: string;
  description: string;
  year: string | number;
  display_order: number;
  image?: string;
  created_at?: string;
  updated_at?: string;
}

export const galleryCategoriesAPI = {
  getCategoryList: async (): Promise<GalleryCategory[]> => {
    const response = await userApiClient.get<{ data: GalleryCategory[] }>(
      "/jbf/gallery-categories/",
      {
        params: { _: Date.now() },
      }
    );
    const data = response.data?.data;
    const items = Array.isArray(data)
      ? data
      : Array.isArray(response.data)
        ? response.data
        : [];
    return (items as GalleryCategory[]).filter(c => c.is_enabled);
  },
};

export const galleryAPI = {
  getGalleryList: async (
    params?: Record<string, unknown>
  ): Promise<GalleryItem[]> => {
    const response = await userApiClient.get<{ data: GalleryItem[] }>(
      "/jbf/gallery/",
      {
        params: { ...params, _: Date.now() },
      }
    );
    const data = response.data?.data;
    const items = Array.isArray(data)
      ? data
      : Array.isArray(response.data)
        ? response.data
        : [];
    return items as GalleryItem[];
  },
};

// ============================================================================
// Donation schemes & main categories (public user portal)
// ============================================================================

export interface DonationMainCategory {
  id: number | string;
  name: string;
  is_active?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface DonationScheme {
  id: number | string;
  title: string;
  sub_title?: string | null;
  type_of_donation?: number | string | null;
  type_of_donation_name?: string | null;
  category?: number | string | null;
  category_name?: string | null;
  sub_category?: number | string | null;
  sub_category_title?: string | null;
  description?: string | null;
  amount_needed?: string | number | null;
  amount_collected?: string | number | null;
  is_active?: boolean;
  start_date?: string | null;
  end_date?: string | null;
  date_status?: "active" | "live" | "upcoming" | "past" | string | null;
  media?: string[] | null;
  banner?: string | null;
  banner_image?: string | null;
  before_image?: string[] | { url?: string; file?: string }[] | null;
  after_image?: string[] | { url?: string; file?: string }[] | null;
  before_media?: string[] | null;
  after_media?: string[] | null;
  before_details?: string | null;
  after_details?: string | null;
  allow_donation_after_target?: boolean;
  is_cash_donation?: boolean;
  amount_type?: "fixed" | "variable" | string | null;
  fixed_amount?: string | number | null;
  min_contribution?: string | number | null;
  max_contribution?: string | number | null;
  creator_type?: string | null;
  creator_name?: string | null;
  institution?: number | string | null;
  institution_name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface DonationNestedList<T> {
  data?: T[];
  results?: T[];
  count?: number;
}

interface DonationListResponse<T> {
  message?: string;
  status_code?: number;
  count?: number;
  results?: T[];
  data?: T[] | DonationNestedList<T>;
}

function extractListData<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) {
    return payload as T[];
  }
  if (!payload || typeof payload !== "object") {
    return [];
  }

  const obj = payload as DonationListResponse<T>;

  if (Array.isArray(obj.data)) {
    return obj.data;
  }
  if (Array.isArray(obj.results)) {
    return obj.results;
  }

  const nested =
    obj.data && typeof obj.data === "object" && !Array.isArray(obj.data)
      ? obj.data
      : undefined;
  if (Array.isArray(nested?.data)) {
    return nested.data;
  }
  if (Array.isArray(nested?.results)) {
    return nested.results;
  }

  return [];
}

export const donationMainCategoryAPI = {
  /** GET /jbf/donation-main-category-master/ */
  list: async (): Promise<DonationMainCategory[]> => {
    try {
      const response = await userApiClient.get<unknown>(
        "/jbf/donation-main-category-master/",
        { params: { page_size: 1000 } }
      );
      return extractListData<DonationMainCategory>(response.data).filter(
        c => c.is_active !== false
      );
    } catch {
      return [];
    }
  },
};

export const donationSchemesAPI = {
  /** GET /jbf/donation-schemes/ */
  list: async (params?: {
    page?: number;
    page_size?: number;
    category?: number | string;
    is_active?: boolean;
  }): Promise<{ schemes: DonationScheme[]; count: number }> => {
    try {
      const query: Record<string, string | number | boolean> = {
        page: params?.page ?? 1,
        page_size: params?.page_size ?? 1000,
      };
      if (
        params?.category !== undefined &&
        params.category !== null &&
        String(params.category) !== ""
      ) {
        query.category = params.category;
      }
      if (params?.is_active !== undefined && params.is_active !== null) {
        query.is_active = params.is_active;
      }

      const response = await userApiClient.get<unknown>(
        "/jbf/donation-schemes/",
        { params: query }
      );
      const raw = response.data;
      const body: DonationListResponse<DonationScheme> =
        raw && typeof raw === "object"
          ? (raw as DonationListResponse<DonationScheme>)
          : {};

      const schemes = extractListData<DonationScheme>(body).filter(
        s => s.is_active !== false
      );
      const nested =
        body.data && typeof body.data === "object" && !Array.isArray(body.data)
          ? body.data
          : undefined;
      const count =
        body.count ?? nested?.count ?? body.results?.length ?? schemes.length;

      return {
        schemes,
        count,
      };
    } catch {
      return {
        schemes: [],
        count: 0,
      };
    }
  },

  get: async (id: string | number): Promise<DonationScheme | null> => {
    try {
      const response = await userApiClient.get<
        DonationScheme | { data?: DonationScheme }
      >(`/jbf/donation-schemes/${id}/`);
      const body = response.data;
      if (body && typeof body === "object" && "data" in body && body.data) {
        return body.data;
      }
      return (body as DonationScheme) ?? null;
    } catch {
      return null;
    }
  },
};

// ============================================================================
// Donation master (donor lookup by mobile)
// ============================================================================

export interface DonationMaster {
  id: string | number;
  name: string;
  email: string;
  phone: string;
  donation_type?: string;
  amount?: string | number;
  message?: string;
  organization?: string;
  pan_number?: string;
  aadhaar_number?: string | null;
  mode?: string;
  payment_mode?: string;
  address_line1?: string;
  address_line2?: string;
  state?: string | number;
  state_id?: string | number;
  district?: string | number;
  district_id?: string | number;
  city?: string | number;
  city_id?: string | number;
  pincode?: string;
}

export const donationMasterAPI = {
  /** GET /jbf/donation-master/?search={query} — search by phone/name/email */
  searchByPhone: async (phone: string): Promise<DonationMaster | null> => {
    try {
      const response = await userApiClient.get<
        | { data?: DonationMaster[]; results?: DonationMaster[] }
        | DonationMaster[]
      >(`/jbf/donation-master/?search=${encodeURIComponent(phone)}`);
      const res = response.data;
      let list: DonationMaster[] = [];
      if (Array.isArray(res)) {
        list = res;
      } else if (res && typeof res === "object") {
        if ("results" in res && Array.isArray(res.results)) {
          list = res.results;
        } else if ("data" in res && Array.isArray(res.data)) {
          list = res.data;
        }
      }
      return list.length > 0 ? list[0] : null;
    } catch {
      return null;
    }
  },
};

// ============================================================================
// Donor auth (OTP for new donors during donation flow)
// ============================================================================

export interface DonorAuthResponse {
  message?: string;
  access?: string;
  refresh?: string;
  jbf_role?: string;
  user?: {
    id: string;
    jbf_role?: string;
    [key: string]: unknown;
  };
  profile?: Record<string, unknown>;
}

export interface DonorMyProfileResponse {
  message?: string;
  status_code?: number;
  data?: Record<string, unknown>;
}

export type DonorOtpMode = "register" | "login";

export interface DonorSendOtpResult {
  message: string;
  code_reference: string;
  mode: DonorOtpMode;
}

export class DonorLoginRequiredError extends Error {
  code_reference: string;
  mode: DonorOtpMode = "login";

  constructor(message: string, code_reference: string) {
    super(message);
    this.name = "DonorLoginRequiredError";
    this.code_reference = code_reference;
  }
}

function getDonorAuthError(error: unknown): { code?: string; message: string } {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as {
      error?: { code?: string; message?: string };
      message?: string;
    };
    return {
      code: data?.error?.code,
      message:
        data?.error?.message ??
        data?.message ??
        error.message ??
        "Authentication failed",
    };
  }
  if (error instanceof Error) {
    return { message: error.message };
  }
  return { message: "Authentication failed" };
}

function isPhoneNumberExistsError(error: unknown): boolean {
  const { code, message } = getDonorAuthError(error);
  return (
    code === "phone_number_exists" ||
    message.toLowerCase().includes("phone number already exists")
  );
}

function extractCodeReference(data?: {
  code_reference?: string;
  message?: string;
  data?: { code_reference?: string; message?: string };
}): { codeReference: string; message: string } {
  const codeReference =
    data?.code_reference ?? data?.data?.code_reference ?? "";
  const message = data?.message ?? data?.data?.message ?? "OTP sent";
  return { codeReference, message };
}

async function completeDonorAuth(
  data: DonorAuthResponse
): Promise<DonorAuthResponse> {
  if (!data?.access) {
    return data;
  }

  const donorRole = data.jbf_role ?? data.user?.jbf_role ?? "donor";
  setLocalAccessToken(data.access);
  if (data.refresh) {
    setLocalRefreshToken(data.refresh);
  }
  setLocalUserInfo({
    ...(data.user ?? {}),
    jbf_role: donorRole,
  });

  try {
    const { data: profileResponse } =
      await userApiClient.post<DonorMyProfileResponse>("/jbf/my-profile/", {
        jbf_role: donorRole,
      });
    const profileData = profileResponse?.data ?? profileResponse;
    const mergedUser = {
      ...getUserInfo(),
      ...(typeof profileData === "object" ? profileData : {}),
      jbf_role: donorRole,
    };
    setLocalUserInfo(mergedUser);
    return {
      ...data,
      jbf_role: donorRole,
      profile:
        typeof profileData === "object" && profileData !== null
          ? (profileData as Record<string, unknown>)
          : undefined,
    };
  } catch (profileError) {
    if (import.meta.env.DEV) {
      console.warn("Failed to set donor my-profile:", profileError);
    }
    return {
      ...data,
      jbf_role: donorRole,
    };
  }
}

export const donorAuthAPI = {
  /** POST /auth/ — send OTP for donor registration */
  sendRegisterOTP: async (phoneNumber: string): Promise<DonorSendOtpResult> => {
    const phoneCode = import.meta.env.VITE_PHONE_CODE as string;
    const { data } = await userApiClient.post<{
      message?: string;
      code_reference?: string;
      data?: { code_reference?: string; message?: string };
    }>("/auth/", {
      phone_number: phoneNumber,
      phone_code: phoneCode,
      jbf_role: "donor",
    });
    const { codeReference, message } = extractCodeReference(data);
    if (!codeReference) {
      throw new Error(message || "No code_reference received");
    }
    return {
      message,
      code_reference: codeReference,
      mode: "register",
    };
  },

  /** POST /auth/login/ — send OTP for existing user login */
  sendLoginOTP: async (phoneNumber: string): Promise<DonorSendOtpResult> => {
    const phoneCode = import.meta.env.VITE_PHONE_CODE as string;
    const { data } = await userApiClient.post<{
      message?: string;
      code_reference?: string;
      data?: { code_reference?: string; message?: string };
    }>("/auth/login/", {
      phone_number: phoneNumber,
      phone_code: phoneCode,
    });
    const { codeReference, message } = extractCodeReference(data);
    if (!codeReference) {
      throw new Error(message || "No code_reference received");
    }
    return {
      message,
      code_reference: codeReference,
      mode: "login",
    };
  },

  /**
   * Send OTP for donation flow.
   * Tries register first; if phone already exists, falls back to login OTP.
   */
  sendOTP: async (phoneNumber: string): Promise<DonorSendOtpResult> => {
    try {
      return await donorAuthAPI.sendRegisterOTP(phoneNumber);
    } catch (error) {
      if (!isPhoneNumberExistsError(error)) {
        throw new Error(getDonorAuthError(error).message);
      }
      const loginOtp = await donorAuthAPI.sendLoginOTP(phoneNumber);
      return {
        ...loginOtp,
        message:
          "This number is already registered. Login OTP sent to your mobile.",
      };
    }
  },

  /** POST /jbf/my-profile/ — set donor role after OTP verification */
  setMyProfile: async (
    jbfRole: string = "donor"
  ): Promise<DonorMyProfileResponse> => {
    const { data } = await userApiClient.post<DonorMyProfileResponse>(
      "/jbf/my-profile/",
      { jbf_role: jbfRole }
    );
    return data;
  },

  /** Verify OTP using register or login endpoint, then set donor profile */
  verifyOTP: async (
    otp: string,
    codeReference: string,
    mode: DonorOtpMode = "register",
    phoneNumber?: string
  ): Promise<DonorAuthResponse> => {
    const endpoint = mode === "login" ? "/auth/login/" : "/auth/";
    const payload =
      mode === "login"
        ? {
            otp: otp.trim(),
            code_reference: codeReference.trim(),
          }
        : {
            otp: otp.trim(),
            code_reference: codeReference.trim(),
            jbf_role: "donor",
          };

    try {
      const { data } = await userApiClient.post<DonorAuthResponse>(
        endpoint,
        payload
      );
      return await completeDonorAuth(data);
    } catch (error) {
      if (
        mode === "register" &&
        isPhoneNumberExistsError(error) &&
        phoneNumber
      ) {
        const loginOtp = await donorAuthAPI.sendLoginOTP(phoneNumber);
        throw new DonorLoginRequiredError(
          "This number is already registered. A login OTP has been sent. Please enter it to continue.",
          loginOtp.code_reference
        );
      }
      throw new Error(getDonorAuthError(error).message);
    }
  },

  /** POST /auth/resend-otp/ — resend OTP */
  resendOTP: async (
    phone: string,
    codeReference: string
  ): Promise<{ message: string; code_reference?: string }> => {
    const { data } = await userApiClient.post<{
      message?: string;
      code_reference?: string;
    }>("/auth/resend-otp/", {
      phone,
      code_reference: codeReference.trim(),
    });
    return {
      message: data?.message ?? "OTP resent",
      code_reference: data?.code_reference,
    };
  },
};

export { parseVendorCategory };

// ============================================================================
// Projects & Initiatives API
// ============================================================================

export interface ProjectMetric {
  project_metric: string;
  details: string;
}

export interface Project {
  id: number;
  title: string;
  short_description: string;
  long_description: string;
  status: string;
  jbf_funds_mobilised: number | string;
  funds_required: number | string;
  outcomes: string;
  project_metrics: string | ProjectMetric[];
  address_line_1: string;
  address_line_2?: string;
  city: string | number;
  state: string | number;
  district?: string | number | null;
  state_name?: string | null;
  district_name?: string | null;
  city_name?: string | null;
  country: string;
  pincode: string;
  map_link?: string;
  mobile_no: string;
  email: string;
  before_media?: string | string[] | null;
  after_media?: string | string[] | null;
  banner_image?: string | string[] | null;
  created_at?: string;
  updated_at?: string;
}

export const projectsAPI = {
  list: async (): Promise<Project[]> => {
    try {
      const { data } = await userApiClient.get<
        { data?: Project[]; results?: Project[] } | Project[]
      >("/jbf/projects/");
      const payload =
        data && typeof data === "object" && "data" in data ? data.data : data;
      if (Array.isArray(payload)) {
        return payload;
      }
      const dataObj = data as any;
      if (dataObj && typeof dataObj === "object") {
        if (Array.isArray(dataObj.results)) return dataObj.results;
        if (Array.isArray(dataObj.data)) return dataObj.data;
      }
      return [];
    } catch {
      return [];
    }
  },
  get: async (id: string | number): Promise<Project | null> => {
    try {
      const { data } = await userApiClient.get<Project | { data?: Project }>(
        `/jbf/projects/${id}/`
      );
      if (data && typeof data === "object") {
        if ("data" in data && data.data) {
          return data.data as Project;
        }
        return data as Project;
      }
      return null;
    } catch {
      return null;
    }
  },
};
